// Map Shopify's standard `shopify.color-pattern` metafield to our 8 color slugs.
//
// The metafield is `list.metaobject_reference` pointing at color taxonomy
// metaobjects. Each referenced metaobject has a `label` field like
// "White", "Red", "Sage", etc. We normalize that label and bucket it into
// the slugs used by /farver/<slug>.

import type { ShopifyProduct } from "./shopify";

const LABEL_TO_SLUG: Array<[RegExp, string]> = [
  [/\b(pink|rose|rosa|fuchsia)\b/i, "lyserod"],
  [/\b(yellow|mustard)\b/i, "gul"],
  [/\b(green|sage|olive|mint|lime|teal)\b/i, "gron"],
  [/\b(blue|navy|cobalt|cyan|turquoise|aqua|aquamarine)\b/i, "blaa"],
  [/\b(red|burgundy|maroon|crimson|ruby|wine)\b/i, "rod"],
  [/\b(orange|peach|terracotta|terra cotta|coral|apricot|rust)\b/i, "orange"],
  [/\b(purple|violet|lavender|lilac|magenta|plum|amethyst)\b/i, "lilla"],
  [/\b(brass|messing|gold|golden|bronze|amber)\b/i, "messing"],
  [/\b(beige|cream|ivory|tan|taupe|sand|ecru|khaki)\b/i, "beige"],
  [/\b(white|off-?white|opal|milk|chalk|latte)\b/i, "hvid"],
];

function slugFromLabel(label: string | null | undefined): string | null {
  if (!label) return null;
  for (const [re, slug] of LABEL_TO_SLUG) {
    if (re.test(label)) return slug;
  }
  return null;
}

/**
 * Extract our color slugs from a Shopify product's `colorMetafield`.
 * Returns an array (a product can be tagged with multiple colors in Shopify).
 */
export function getColorSlugsFromProduct(node: ShopifyProduct["node"]): string[] {
  const mf = node.colorMetafield;
  if (!mf) return [];
  const slugs = new Set<string>();

  // References path (list.metaobject_reference): the canonical source.
  const refs = mf.references?.nodes ?? [];
  for (const ref of refs) {
    const labelField = ref.fields?.find((f) => f.key === "label");
    const slug = slugFromLabel(labelField?.value);
    if (slug) slugs.add(slug);
  }

  // Fallback: try parsing the raw `value` (sometimes a plain string for
  // single-color metafields, or a JSON array of GIDs we cannot resolve here).
  if (slugs.size === 0 && mf.value) {
    const raw = mf.value.trim();
    if (raw.startsWith("[")) {
      // JSON array of GIDs — no labels available without references.
    } else {
      const slug = slugFromLabel(raw);
      if (slug) slugs.add(slug);
    }
  }

  return Array.from(slugs);
}
